import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Navigation } from '../../home/components/Navigation';
import { Footer } from '../../home/components/Footer';
import { ServiceModal } from '../../home/components/ServiceModal';
import { IndustriesModal } from '../../home/components/IndustriesModal';
import { HowItWorksModal } from '../../home/components/HowItWorksModal';
import { CalendarModal } from '../../home/components/CalendarModal';
import { SEO } from '../../../components/SEO';

const faqs = [
  {
    question: 'How does AI for contractors work?',
    answer: 'Our AI handles inbound calls, website chat, appointment scheduling, and automated follow-up — all trained on your specific business. When a customer reaches out, your AI responds instantly, answers their questions, and books the job. You get notified and focus on the field.',
  },
  {
    question: 'Will the AI know about my services and pricing?',
    answer: 'Yes. We train your AI assistant on your business — your service areas, project types, pricing structure, and scheduling availability. It speaks confidently about your contracting business so every customer gets accurate, helpful answers.',
  },
  {
    question: 'What happens when a customer calls after hours?',
    answer: 'Your AI receptionist answers 24/7. It schedules estimate appointments, captures lead info, and handles common questions even at 10pm on a Sunday. You wake up to a full inbox instead of missed opportunities.',
  },
  {
    question: 'Can it book jobs directly onto my calendar?',
    answer: 'Absolutely. Our AI integrates with your calendar, checks availability in real time, and books estimate appointments without any back-and-forth. Zero double-bookings, zero phone tag.',
  },
  {
    question: 'How quickly can I get set up?',
    answer: 'Most contractors are live within 24–48 hours. We handle the entire setup, train the AI on your business, and test everything before going live. No technical knowledge needed on your end.',
  },
  {
    question: 'Is it affordable for a small contracting business?',
    answer: 'Our plans start at a fraction of the cost of a part-time receptionist. Most contractors find that capturing just one additional job per month from after-hours inquiries more than covers the monthly cost.',
  },
];

const painPoints = [
  {
    icon: 'ri-phone-line',
    title: 'Missing Calls on the Job Site',
    desc: 'You\'re on a roof, in a crawl space, or running a crew. Your phone rings and you can\'t answer. That lead? Gone — they already called your competitor.',
  },
  {
    icon: 'ri-moon-line',
    title: 'Losing Leads After Hours',
    desc: 'Homeowners don\'t search for contractors only during business hours. After-hours inquiries with no response mean lost jobs you never even knew about.',
  },
  {
    icon: 'ri-calendar-close-line',
    title: 'Manual Scheduling Headaches',
    desc: 'Back-and-forth texts and calls just to book an estimate. Double-bookings. No-shows. Scheduling should never eat this much of your day.',
  },
  {
    icon: 'ri-time-line',
    title: 'Slow or No Follow-Up',
    desc: 'Most contractors follow up once — maybe. Leads who don\'t hear back in 5 minutes are already talking to someone else. Speed wins jobs.',
  },
];

const solutions = [
  {
    icon: 'ri-phone-line',
    title: 'AI Phone Answering — 24/7',
    desc: 'Your AI receptionist picks up every call, immediately. It answers questions, captures lead info, and schedules appointments even when you\'re on a job site or asleep.',
    link: '/services/ai-receptionist',
  },
  {
    icon: 'ri-chat-smile-3-line',
    title: 'AI Chatbot for Your Website',
    desc: 'Capture leads from your website around the clock. Your AI chatbot engages every visitor, qualifies them, and converts them into booked estimate appointments automatically.',
    link: '/services/ai-chatbot',
  },
  {
    icon: 'ri-calendar-check-line',
    title: 'Automated Scheduling',
    desc: 'No more phone tag. Customers book estimate appointments directly — your AI checks your calendar in real time and confirms instantly. Clean, organized, zero effort.',
    link: '/services/appointment-scheduling',
  },
  {
    icon: 'ri-mail-send-line',
    title: 'Automated Lead Follow-Up',
    desc: 'AI sends timely follow-up messages to every new lead, estimate no-show, and unbooked inquiry. Stay top of mind and close more jobs without lifting a finger.',
    link: null,
  },
];

const benefits = [
  { icon: 'ri-flashlight-line', title: 'Capture Every Lead', desc: 'No more missed calls or after-hours black holes. Every inquiry is caught, logged, and acted on instantly.' },
  { icon: 'ri-briefcase-4-line', title: 'Book More Jobs Automatically', desc: 'AI books estimate appointments around the clock. Your calendar fills up while you focus on delivering great work.' },
  { icon: 'ri-time-line', title: 'Save Hours on Admin Every Week', desc: 'Cut out the calls, texts, and scheduling back-and-forth that steal time from your actual work.' },
  { icon: 'ri-trophy-line', title: 'Never Miss an Opportunity', desc: 'AI for contractors means 24/7 coverage — weekends, evenings, holidays — so every opportunity gets a response.' },
];

const steps = [
  {
    number: '01',
    icon: 'ri-settings-4-line',
    title: 'We Set Up Your System',
    desc: 'Our team configures and trains your AI on your business — services, pricing, service area, calendar, and FAQs. You\'re live in 24–48 hours with zero tech work on your end.',
  },
  {
    number: '02',
    icon: 'ri-robot-line',
    title: 'AI Handles Calls & Messages',
    desc: 'Your AI receptionist answers every call, responds to every website inquiry, and books appointments — 24/7. You get instant notifications for every new lead.',
  },
  {
    number: '03',
    icon: 'ri-hand-coin-line',
    title: 'You Close More Jobs',
    desc: 'Walk into every estimate with a pre-qualified lead. No more chasing. Your pipeline is full, your calendar is organized, and your revenue grows.',
  },
];

const useCases = [
  {
    icon: 'ri-home-8-line',
    title: 'Roofing Companies',
    desc: 'Storm season calls flood in — your AI handles every one, books inspections, and captures insurance claim leads before competitors even pick up.',
    link: '/ai-for-roofers',
  },
  {
    icon: 'ri-plant-line',
    title: 'Landscapers',
    desc: 'Spring is your busiest season. Let AI book lawn care, cleanups, and landscaping consultations automatically while your crew is in the field.',
    link: '/ai-for-landscapers',
  },
  {
    icon: 'ri-drop-line',
    title: 'Plumbers',
    desc: 'Emergency calls come at 2am. Your AI answers, qualifies the issue, dispatches the right response, and gets the customer booked — instantly.',
    link: null,
  },
  {
    icon: 'ri-temp-hot-line',
    title: 'HVAC Companies',
    desc: 'Peak season demand shouldn\'t mean unanswered calls. AI for contractors in HVAC means every service call, tune-up, and emergency is captured and scheduled.',
    link: null,
  },
];

export default function AIForContractors() {
  const [isServiceModalOpen, setIsServiceModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState('');
  const [isIndustriesModalOpen, setIsIndustriesModalOpen] = useState(false);
  const [isHowItWorksModalOpen, setIsHowItWorksModalOpen] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  const handleOpenService = (title: string) => {
    setSelectedService(title);
    setIsServiceModalOpen(true);
  };

  return (
    <>
      <SEO
        title="AI for Contractors | AI Automation for Contracting Businesses | Dino Nuggets"
        description="Stop missing contractor leads. AI for contractors from Dino Nuggets answers calls 24/7, books estimate appointments automatically, and follows up on every lead so you close more jobs."
        canonical="/industries/ai-for-contractors"
        schemas={[
          {
            type: 'Service',
            data: {
              '@type': 'Service',
              name: 'AI Automation for Contractors',
              description: 'AI phone answering, automated scheduling, and lead follow-up designed specifically for contractors and field service businesses.',
              provider: { '@type': 'Organization', name: 'Dino Nuggets' },
              areaServed: 'United States',
            },
          },
          {
            type: 'FAQPage',
            data: {
              '@type': 'FAQPage',
              mainEntity: faqs.map(f => ({
                '@type': 'Question',
                name: f.question,
                acceptedAnswer: { '@type': 'Answer', text: f.answer },
              })),
            },
          },
        ]}
      />

      <div className="min-h-screen bg-white">
        <Navigation />

        {/* ── HERO ────────────────────────────────────────────── */}
        <section className="relative pt-28 pb-24 px-6 lg:px-8 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="/readdy-assets/contractors-hero-v2-001.jpg"
              alt="AI automation for contractors"
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/80 to-white/40"></div>

          <div className="relative max-w-7xl mx-auto">
            <div className="max-w-2xl">
              <span className="inline-block bg-primary/10 text-primary text-sm font-semibold px-4 py-1.5 rounded-full mb-6 tracking-wide uppercase">
                AI for Contractors
              </span>
              <h1 className="font-display text-5xl lg:text-6xl font-extrabold text-gray-900 mb-6 leading-tight">
                AI Automation<br />
                <span className="text-primary">for Contractors</span>
              </h1>
              <p className="text-xl text-gray-700 mb-10 leading-relaxed">
                Stop missing calls, book more jobs, and automate your business with AI tools built for contractors. Never lose another lead to voicemail again.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => setIsCalendarOpen(true)}
                  className="bg-primary hover:bg-primary/90 text-white px-8 py-4 rounded-lg font-semibold transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer text-lg"
                >
                  Book a Free Consultation
                </button>
                <button
                  onClick={() => setIsCalendarOpen(true)}
                  className="border-2 border-gray-300 hover:border-primary hover:text-primary text-gray-700 px-8 py-4 rounded-lg font-semibold transition-all whitespace-nowrap cursor-pointer text-lg"
                >
                  See How It Works
                </button>
              </div>
              <div className="flex items-center gap-6 mt-8">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <i className="ri-check-line text-primary text-lg w-5 h-5 flex items-center justify-center"></i>
                  <span>Live in 24–48 hours</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <i className="ri-check-line text-primary text-lg w-5 h-5 flex items-center justify-center"></i>
                  <span>No tech skills needed</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <i className="ri-check-line text-primary text-lg w-5 h-5 flex items-center justify-center"></i>
                  <span>Cancel anytime</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── STATS BAR ───────────────────────────────────────── */}
        <section className="bg-secondary py-10 px-6 lg:px-8">
          <div className="max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-3 gap-8 text-center">
            {[
              { stat: '70%', label: 'of callers hang up rather than leave a voicemail' },
              { stat: '5 min', label: 'is all it takes before your lead calls the next contractor' },
              { stat: '3×', label: 'more jobs closed by contractors who respond in the first hour' },
            ].map(item => (
              <div key={item.stat} className="flex flex-col items-center">
                <span className="text-4xl font-extrabold text-primary mb-2">{item.stat}</span>
                <p className="text-gray-400 text-sm leading-relaxed max-w-xs">{item.label}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ── PAIN POINTS ─────────────────────────────────────── */}
        <section className="py-20 px-6 lg:px-8 bg-gray-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-14">
              <span className="inline-block bg-red-50 text-red-500 text-sm font-semibold px-4 py-1.5 rounded-full mb-4 uppercase tracking-wide">Sound Familiar?</span>
              <h2 className="font-display text-4xl font-bold text-gray-900 mb-4">
                The Problems Holding Contractors Back
              </h2>
              <p className="text-gray-600 text-lg max-w-2xl mx-auto leading-relaxed">
                Running a contracting business is hard enough. These daily frustrations are costing you real revenue — and AI for contractors fixes every one of them. Explore how we help <Link to="/ai-for-roofers" className="text-primary hover:underline font-medium">roofing companies</Link> and <Link to="/ai-for-landscapers" className="text-primary hover:underline font-medium">landscapers</Link> with the same automation.
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {painPoints.map(item => (
                <div key={item.title} className="bg-white p-7 rounded-xl border border-gray-200 hover:border-red-200 transition-all group">
                  <div className="w-12 h-12 flex items-center justify-center bg-red-50 rounded-lg mb-5 group-hover:bg-red-100 transition-colors">
                    <i className={`${item.icon} text-2xl text-red-500`}></i>
                  </div>
                  <h3 className="font-semibold text-gray-900 text-lg mb-3">{item.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── SOLUTION ────────────────────────────────────────── */}
        <section className="py-20 px-6 lg:px-8 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <span className="inline-block bg-primary/10 text-primary text-sm font-semibold px-4 py-1.5 rounded-full mb-4 uppercase tracking-wide">The Solution</span>
                <h2 className="font-display text-4xl font-bold text-gray-900 mb-6 leading-tight">
                  How Dino Nuggets Solves It with AI Automation
                </h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Our platform combines <Link to="/services/ai-receptionist" className="text-primary hover:underline font-medium">AI phone answering</Link>, <Link to="/services/ai-chatbot" className="text-primary hover:underline font-medium">intelligent chatbot</Link>, <Link to="/services/appointment-scheduling" className="text-primary hover:underline font-medium">automated scheduling</Link>, and smart follow-up — all working together as a seamless system built specifically for contractors.
                </p>
                <p className="text-gray-700 leading-relaxed mb-8">
                  AI for contractors isn't a gimmick — it's the same technology that enterprise companies use, now purpose-built and priced for small and mid-size contracting businesses across the country.
                </p>
                <button
                  onClick={() => setIsCalendarOpen(true)}
                  className="bg-primary hover:bg-primary/90 text-white px-7 py-3.5 rounded-lg font-semibold transition-all whitespace-nowrap cursor-pointer"
                >
                  Book a Free Consultation
                </button>
              </div>
              <div className="space-y-5">
                {solutions.map(item => (
                  <div key={item.title} className="flex gap-5 p-6 bg-gray-50 rounded-xl hover:bg-primary/5 transition-colors group">
                    <div className="w-12 h-12 flex items-center justify-center bg-primary/10 rounded-lg flex-shrink-0 group-hover:bg-primary/20 transition-colors">
                      <i className={`${item.icon} text-2xl text-primary`}></i>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 text-lg mb-1.5">
                        {item.link ? (
                          <Link to={item.link} className="hover:text-primary transition-colors cursor-pointer">{item.title}</Link>
                        ) : item.title}
                      </h3>
                      <p className="text-gray-600 text-sm leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── BENEFITS ────────────────────────────────────────── */}
        <section className="py-20 px-6 lg:px-8 bg-gradient-to-br from-secondary to-secondary/90 text-white">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-14">
              <span className="inline-block bg-white/10 text-white text-sm font-semibold px-4 py-1.5 rounded-full mb-4 uppercase tracking-wide">Why It Works</span>
              <h2 className="font-display text-4xl font-bold text-white mb-4">
                The Benefits of AI for Contractors
              </h2>
              <p className="text-gray-400 text-lg max-w-2xl mx-auto leading-relaxed">
                Built for field service pros who don't have time to babysit a phone. Here's what changes when your AI goes live.
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {benefits.map(item => (
                <div key={item.title} className="bg-white/5 border border-white/10 p-7 rounded-xl hover:bg-white/10 transition-all">
                  <div className="w-12 h-12 flex items-center justify-center bg-primary/20 rounded-lg mb-5">
                    <i className={`${item.icon} text-2xl text-primary`}></i>
                  </div>
                  <h3 className="font-semibold text-white text-lg mb-3">{item.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── HOW IT WORKS ────────────────────────────────────── */}
        <section className="py-20 px-6 lg:px-8 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-14">
              <span className="inline-block bg-primary/10 text-primary text-sm font-semibold px-4 py-1.5 rounded-full mb-4 uppercase tracking-wide">Simple Process</span>
              <h2 className="font-display text-4xl font-bold text-gray-900 mb-4">
                How AI for Contractors Works
              </h2>
              <p className="text-gray-600 text-lg max-w-2xl mx-auto leading-relaxed">
                Three simple steps to a fully automated contracting business. No tech headaches, no long onboarding.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-8 relative">
              <div className="hidden md:block absolute top-16 left-1/3 right-1/3 h-px bg-gradient-to-r from-primary/30 via-primary to-primary/30"></div>
              {steps.map((step, idx) => (
                <div key={step.number} className="relative text-center">
                  <div className="relative inline-flex">
                    <div className="w-16 h-16 flex items-center justify-center bg-primary rounded-2xl mb-6 mx-auto shadow-lg">
                      <i className={`${step.icon} text-3xl text-white`}></i>
                    </div>
                    <span className="absolute -top-2 -right-2 w-7 h-7 flex items-center justify-center bg-secondary text-white text-xs font-bold rounded-full border-2 border-white">
                      {idx + 1}
                    </span>
                  </div>
                  <h3 className="font-display font-bold text-xl text-gray-900 mb-3">{step.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed max-w-xs mx-auto">{step.desc}</p>
                </div>
              ))}
            </div>
            <div className="text-center mt-12">
              <button
                onClick={() => setIsCalendarOpen(true)}
                className="bg-primary hover:bg-primary/90 text-white px-8 py-4 rounded-lg font-semibold transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer text-lg"
              >
                Book a Free Consultation
              </button>
            </div>
          </div>
        </section>

        {/* ── USE CASE EXAMPLES ───────────────────────────────── */}
        <section className="py-20 px-6 lg:px-8 bg-gray-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-14">
              <span className="inline-block bg-primary/10 text-primary text-sm font-semibold px-4 py-1.5 rounded-full mb-4 uppercase tracking-wide">Industries We Serve</span>
              <h2 className="font-display text-4xl font-bold text-gray-900 mb-4">
                AI Automation Works for Every Contracting Trade
              </h2>
              <p className="text-gray-600 text-lg max-w-2xl mx-auto leading-relaxed">
                Whether you swing a hammer or fix a furnace, our AI for contractors platform adapts to your trade and workflow.
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {useCases.map(item => (
                <div key={item.title} className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:border-primary hover:shadow-md transition-all group">
                  <div className="h-44 overflow-hidden">
                    <img
                      src={
                        item.title === 'Roofing Companies'
                          ? '/readdy-assets/usecase-roofing-001.jpg'
                          : item.title === 'Landscapers'
                          ? '/readdy-assets/usecase-landscaping-001.jpg'
                          : item.title === 'Plumbers'
                          ? '/readdy-assets/usecase-plumbing-001.jpg'
                          : '/readdy-assets/usecase-hvac-001.jpg'
                      }
                      alt={item.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-9 h-9 flex items-center justify-center bg-primary/10 rounded-lg">
                        <i className={`${item.icon} text-xl text-primary`}></i>
                      </div>
                      <h3 className="font-semibold text-gray-900 text-lg">
                        {item.link ? (
                          <Link to={item.link} className="hover:text-primary transition-colors cursor-pointer">{item.title}</Link>
                        ) : item.title}
                      </h3>
                    </div>
                    <p className="text-gray-600 text-sm leading-relaxed">{item.desc}</p>
                    {item.link && (
                      <Link
                        to={item.link}
                        className="inline-flex items-center gap-1.5 text-primary text-sm font-medium mt-4 hover:underline cursor-pointer"
                      >
                        Learn more <i className="ri-arrow-right-line"></i>
                      </Link>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── INTERNAL LINKS / SERVICES ───────────────────────── */}
        <section className="py-16 px-6 lg:px-8 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="bg-gradient-to-br from-gray-50 to-primary/5 rounded-2xl p-10">
              <div className="text-center mb-10">
                <h2 className="font-display text-3xl font-bold text-gray-900 mb-3">
                  Explore Our AI Tools for Contractors
                </h2>
                <p className="text-gray-600 leading-relaxed max-w-xl mx-auto">
                  Every contractor's business is different. Mix and match the AI tools that fit your workflow best.
                </p>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { to: '/services/ai-receptionist', icon: 'ri-customer-service-2-line', title: 'AI Receptionist', desc: 'Full virtual front desk — calls, texts, and web chat handled seamlessly.' },
                  { to: '/services/ai-phone-answering', icon: 'ri-phone-line', title: 'AI Phone Answering', desc: 'Live-sounding AI that picks up every contractor call like a pro.' },
                  { to: '/services/appointment-scheduling', icon: 'ri-calendar-2-line', title: 'Appointment Scheduling', desc: 'Automatic booking into your calendar with zero manual effort.' },
                  { to: '/services/ai-chatbot', icon: 'ri-chat-smile-3-line', title: 'AI Website Chatbot', desc: 'Capture web leads 24/7 from your contracting website.' },
                ].map(item => (
                  <Link
                    key={item.to}
                    to={item.to}
                    className="bg-white p-5 rounded-xl border border-gray-200 hover:border-primary hover:shadow-md transition-all cursor-pointer group flex gap-4"
                  >
                    <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
                      <i className={`${item.icon} text-2xl text-primary`}></i>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 group-hover:text-primary transition-colors mb-1 text-sm">{item.title}</h3>
                      <p className="text-xs text-gray-500 leading-relaxed">{item.desc}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── TESTIMONIAL ─────────────────────────────────────── */}
        <section className="py-16 px-6 lg:px-8 bg-gray-50">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-display text-3xl font-bold text-gray-900 mb-3">What Contractors Are Saying</h2>
              <p className="text-gray-600">Real results from real field service businesses using AI automation.</p>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {[
                {
                  quote: 'We used to miss 3–4 calls a day on job sites. Now the AI catches every single one. We booked two extra jobs in the first week alone.',
                  name: 'Marcus T.',
                  role: 'General Contractor, Texas',
                },
                {
                  quote: 'I was skeptical about AI for contractors, but it\'s been a game-changer. My calendar is full and I haven\'t had to play phone tag in months.',
                  name: 'Sandra R.',
                  role: 'Roofing Company Owner, Florida',
                },
                {
                  quote: 'Setup was fast, the AI sounds professional, and the follow-up automation alone has recovered at least 5 leads I would have lost.',
                  name: 'Derek H.',
                  role: 'HVAC Business Owner, Georgia',
                },
              ].map(t => (
                <div key={t.name} className="bg-white p-7 rounded-xl border border-gray-200">
                  <div className="flex gap-1 mb-4">
                    {[1,2,3,4,5].map(s => (
                      <i key={s} className="ri-star-fill text-yellow-400 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-gray-700 text-sm leading-relaxed mb-5 italic">&ldquo;{t.quote}&rdquo;</p>
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">{t.name}</p>
                    <p className="text-gray-500 text-xs">{t.role}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── FAQ ─────────────────────────────────────────────── */}
        <section className="py-20 px-6 lg:px-8 bg-white">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <span className="inline-block bg-primary/10 text-primary text-sm font-semibold px-4 py-1.5 rounded-full mb-4 uppercase tracking-wide">FAQ</span>
              <h2 className="font-display text-4xl font-bold text-gray-900 mb-4">
                Questions About AI for Contractors
              </h2>
              <p className="text-gray-600 leading-relaxed">
                Everything you need to know before getting started.
              </p>
            </div>
            <div className="space-y-3">
              {faqs.map((faq, index) => (
                <div key={index} className="border border-gray-200 rounded-xl overflow-hidden">
                  <button
                    onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                    className="w-full px-6 py-5 text-left flex items-center justify-between hover:bg-gray-50 transition-colors cursor-pointer"
                  >
                    <h3 className="font-semibold text-gray-900 pr-4">{faq.question}</h3>
                    <i className={`ri-arrow-down-s-line text-2xl text-gray-400 flex-shrink-0 transition-transform duration-200 ${openFaqIndex === index ? 'rotate-180' : ''}`}></i>
                  </button>
                  {openFaqIndex === index && (
                    <div className="px-6 pb-5 pt-1">
                      <p className="text-gray-700 leading-relaxed text-sm">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── FINAL CTA ───────────────────────────────────────── */}
        <section className="py-24 px-6 lg:px-8 relative overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="/readdy-assets/cta-bg-contractors-001.jpg"
              alt=""
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-secondary/92"></div>
          </div>
          <div className="relative max-w-3xl mx-auto text-center">
            <span className="inline-block bg-primary/20 text-primary text-sm font-semibold px-4 py-1.5 rounded-full mb-6 uppercase tracking-wide">
              Ready to Grow?
            </span>
            <h2 className="font-display text-4xl lg:text-5xl font-extrabold text-white mb-6 leading-tight">
              Ready to Stop Missing Contractor Leads?
            </h2>
            <p className="text-xl text-gray-300 mb-10 leading-relaxed">
              Book a free 15-minute consultation and see exactly how AI for contractors fits your business. No commitment, no tech headaches, no BS.
            </p>
            <button
              onClick={() => setIsCalendarOpen(true)}
              className="bg-primary hover:bg-primary/90 text-white px-10 py-5 rounded-lg font-bold transition-all shadow-xl hover:shadow-2xl whitespace-nowrap cursor-pointer text-lg"
            >
              Book Your Free Consultation
            </button>
            <p className="text-gray-500 text-sm mt-5">Free 15-min call · No credit card required · Live in 24–48 hours</p>
          </div>
        </section>

        <Footer
          onOpenService={handleOpenService}
          onOpenIndustries={() => setIsIndustriesModalOpen(true)}
          onOpenHowItWorks={() => setIsHowItWorksModalOpen(true)}
        />

        <ServiceModal isOpen={isServiceModalOpen} onClose={() => setIsServiceModalOpen(false)} title={selectedService} />
        <IndustriesModal isOpen={isIndustriesModalOpen} onClose={() => setIsIndustriesModalOpen(false)} />
        <HowItWorksModal isOpen={isHowItWorksModalOpen} onClose={() => setIsHowItWorksModalOpen(false)} />
        <CalendarModal isOpen={isCalendarOpen} onClose={() => setIsCalendarOpen(false)} />
      </div>
    </>
  );
}
